import CreativeType from '@/Enums/Creatives/CreativeType.enum';
import ExportIcon from '@/components/Icons/ExportIcon';
import CreativePreviewModal from '@/pages/CreativeLibrary/Modals/CreativePreviewModal';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { type CreativePreview, fetchCreativePreviews } from '@/store/slices/reportingV2/creativePreviews.api';
import type { ReportRow } from '@/store/slices/reportingV2/reportingV2.api';
import {
  selectReportingV2Filters,
  selectReportingV2Widget,
  setImpressionsByTypeLive,
  setPublisherInventoryActive,
} from '@/store/slices/reportingV2/reportingV2Slice';
import { ArrowDownwardRounded, ArrowUpwardRounded, UnfoldMoreRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  CTR_CLICKABLE_NOTE,
  ENTITY_REACH_NOTE,
  FREQUENCY_LABEL,
  FREQUENCY_NOTE,
  FREQUENCY_UNAVAILABLE_NOTE,
  FREQUENCY_UNAVAILABLE_NOTE_RESTRICTED,
  NOT_ADDITIVE_LABEL,
  REACH_NOT_ADDITIVE_NOTE,
  REACH_UNAVAILABLE_NOTE,
  REACH_UNAVAILABLE_NOTE_RESTRICTED,
  buildTotalRowValues,
  clientMoneyUnavailable,
  compactCurrency,
  currencyPrecise,
  daypartLabel,
  decimal,
  dimensionLabel,
  dspLabel,
  entityLabel,
  genericChannelLabel,
  geoIgnoredFilterNote,
  hourLabel,
  isConversionsColumnShown,
  isConversionsPerPlatformScope,
  isDspEntityScope,
  isPerPlatformScope,
  isPlaceholderDimension,
  isTotalRowMismatched,
  maskedChannelLabelFn,
  num,
  percent,
  percentWhole,
  reachRowNote,
  sumOf,
  totalRowMismatchNote,
  withDeviceDisplay,
  withEntityDsp,
  withMargin,
} from '../format';
import { useAvailableDimensions } from '../hooks/useAvailableDimensions';
import { liveCovers, liveReachKey, useDv360LiveReach } from '../hooks/useDv360LiveReach';
import { useExportWidgetCsv } from '../hooks/useExportWidgetCsv';
import { usePublisherInventory } from '../hooks/usePublisherInventory';
import { useReportWidget } from '../hooks/useReportWidget';
import { useReportingPermissions } from '../hooks/useReportingPermissions';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { COLORS, cardSx } from '../theme';
import ClientMoneyCoverageNote from './ClientMoneyCoverageNote';
import ColumnResizeHandle from './ColumnResizeHandle';
import PublisherInventoryTable from './PublisherInventoryTable';
import WidgetExportButton from './WidgetExportButton';
import WidgetSkeleton from './WidgetSkeleton';

const darkToggleSx = {
  bgcolor: COLORS.pageBg,
  borderRadius: '10px',
  border: `1px solid ${COLORS.border}`,
  p: 0.5,
  '& .MuiToggleButton-root': {
    textTransform: 'none',
    border: 0,
    borderRadius: '8px !important',
    px: 1.5,
    py: 0.25,
    color: COLORS.muted,
    fontSize: 13,
    fontWeight: 600,
    '&.Mui-selected': { bgcolor: COLORS.black, color: '#fff', '&:hover': { bgcolor: '#000' } },
  },
} as const;

// Exported so the Conversions widget (flank#4648) reuses the same tab set
// rather than redefining it — it relabels 'inventory' to 'Publishers' on its
// own TABS copy (the Publishers rollup, #4643, isn't on main yet), but the
// underlying CHIPS/resolve() below are shared as-is.
export const TABS = [
  { key: 'hierarchy', label: 'Hierarchy' },
  { key: 'channel', label: 'Channel' },
  // "Geo", not "Device & Geo": Device moves under Dimensions per #4109.
  { key: 'geo', label: 'Geo' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'dimensions', label: 'Dimensions' },
];

/**
 * Breakdown chips per tab.
 *
 * Every tab now offers its own chips, matching the reference design — previously
 * only Hierarchy had them and the other four tabs were locked to a single
 * hidden dimension each, which is what #4109 items 1-3 reported ("Device & Geo
 * shows only devices", "Inventory does not show app/domain/deal/daypart",
 * "Dimensions do not show product or publisher").
 *
 * `dim === null` means the canonical layer has no backing dimension yet; the
 * chip renders disabled with a reason rather than being silently absent, so the
 * gap reads as "not available yet" instead of "someone forgot it".
 */
type Chip = {
  key: string;
  label: string;
  dims: string[] | null;
  labelKey?: string;
  idKey?: string;
  reason?: string;
  /**
   * How this chip's raw value becomes the text in the name column. REQUIRED —
   * that is the entire point (#4389).
   *
   * This drift has produced four tickets (#4378, #4380, #4385 and #4378 again),
   * every one of them a chip whose raw value wasn't readable and whose author
   * didn't think about it. An optional field plus a test listing the chips we'd
   * already fixed protected only those; the fifth would have slipped through the
   * same way. Required means a new chip does not compile until its author has
   * decided, and `dimensionLabel` is a deliberate "the raw value reads fine"
   * rather than an oversight.
   *
   * The second argument is the row's `idKey` value where the chip declares one,
   * so `entityLabel` can fold an id into rows whose name is blank.
   */
  labelFn: (value: unknown, id?: unknown) => string;
  /**
   * Render an Advertiser column beside the name (#4127 Task 4). Hierarchy rows
   * are campaign/line-item/creative names, which don't say who they belong to.
   */
  showAdvertiser?: boolean;
};

export const CHIPS: Record<string, Chip[]> = {
  // Hierarchy levels: Advertiser -> Campaign -> Line Item -> Creative (#4455).
  // (Previously "Item" and "Creative" both mapped to the creative dimension —
  // identical data — and there was no distinct Line Item level.)
  hierarchy: [
    {
      key: 'advertiser',
      label: 'Advertiser',
      dims: ['advertiser', 'advertiser_name'],
      labelKey: 'advertiser_name',
      idKey: 'advertiser',
      labelFn: entityLabel,
      // No showAdvertiser column here — the row itself IS the advertiser.
    },
    {
      key: 'campaign',
      label: 'Campaign',
      dims: ['advertiser_name', 'campaign', 'campaign_name'],
      labelKey: 'campaign_name',
      idKey: 'campaign',
      labelFn: entityLabel,
      showAdvertiser: true,
    },
    {
      key: 'lineitem',
      label: 'Line Item',
      dims: ['advertiser_name', 'line_item', 'line_item_name'],
      labelKey: 'line_item_name',
      idKey: 'line_item',
      labelFn: entityLabel,
      showAdvertiser: true,
    },
    {
      key: 'creative',
      label: 'Creative',
      dims: ['advertiser_name', 'creative', 'creative_name'],
      labelKey: 'creative_name',
      idKey: 'creative',
      labelFn: entityLabel,
      showAdvertiser: true,
    },
  ],
  channel: [
    { key: 'channel', label: 'Channel', dims: ['channel'], labelFn: entityLabel },
    { key: 'dsp', label: 'Platform', dims: ['dsp'], labelFn: dspLabel },
  ],
  // flank#5183 — Country/County/Congressional removed: not consistent with the
  // Geo map, which only draws State/Market/City/ZIP (see the comment at
  // DashboardBuilder.tsx's GEO_LEVEL_DIMENSIONS).
  geo: [
    { key: 'geo_state', label: 'State', dims: ['geo_state'], labelFn: entityLabel },
    { key: 'geo_dma', label: 'Market', dims: ['geo_dma'], labelFn: entityLabel },
    { key: 'geo_city', label: 'City', dims: ['geo_city'], labelFn: entityLabel },
    // Labelled rather than raw, matching GeographicPerformance: a bare ZIP is
    // unreadable to anyone who doesn't know the market (FB-006). Safe here for the
    // same reason — this tab is a ranked table, so nothing joins boundaries on the
    // value. `key` stays `geo_zip` so saved chip selections keep resolving; the
    // display column follows `dims` (see `label` below).
    // labelFn is load-bearing, not cosmetic: the default render path runs
    // entityLabel -> cleanName, which truncates at the first "|" for
    // Beeswax-style names (flank#4620). `geo_zip_label` is literally "80204 |
    // Denver, CO" — not Beeswax-shaped, but still don't rely on that: an
    // explicit dimensionLabel here is what keeps the city/state this chip
    // exists to show, matching the map card one row up. Dropping it risked
    // the grid going back to bare ZIPs — the same drift #4378 reported,
    // reintroduced by its own fix.
    { key: 'geo_zip', label: 'ZIP', dims: ['geo_zip_label'], labelFn: dimensionLabel },
  ],
  inventory: [
    // Default view for everyone (flank#4643) — app bundles rolled up to the
    // Ribeye Publisher they're mapped to; see PublisherInventoryTable. Not a
    // report() chip like the others: ImpressionsByType special-cases this key
    // to render that component instead of the generic table, so `dims` here
    // only matters for the shared reach/conversions scope gating (same as the
    // `app_name` chip below, since the rollup is built from app_name rows).
    { key: 'publisher', label: 'Publishers', dims: ['app_name'], labelFn: entityLabel },
    // Raw view, gated server-side by #4644's can_view_raw_inventory — hidden
    // from the chip row entirely for a disallowed user (see
    // visibleInventoryChips), never fetched just to find out.
    { key: 'app_name', label: 'App', dims: ['app_name'], labelFn: entityLabel },
    { key: 'domain', label: 'Domain', dims: ['domain'], labelFn: entityLabel },
    // Deal is deliberately absent, not merely disabled: the 2026-08-04 testing
    // review decided not to surface deal identifiers at all until there's a
    // strategy for them, since exposing them prematurely risks conflict. A
    // greyed chip still advertises that the data exists.
  ],
  // Device and Daypart sit here per #4109 rather than under Geo / Inventory.
  // Product + Publisher removed (#4127 Task 5) — no longer aligned with current
  // inventory reporting.
  dimensions: [
    // Same CTV/Console normalisation the Impressions by Device card applies
    // via `deviceLabel` (flank#4882) — without it, this grid read "TV" right
    // below a card showing "CTV" for the identical row.
    { key: 'device', label: 'Device', dims: ['device'], labelFn: withDeviceDisplay(entityLabel) },
    // "By Hour", not "Daypart" (#4127 Task 6): this breakdown was always
    // hour-of-day, and calling it Daypart made 24 hourly rows look like a
    // daypart report. Without labelFn it listed the raw column: 0, 1, 2 ... 23.
    { key: 'hour', label: 'By Hour', dims: ['hour_of_day'], labelFn: hourLabel },
    // True dayparts, aggregated server-side into 6 buckets. Bucketing the hourly
    // rows client-side would return 24 rows carrying 6 repeated labels instead of
    // 6 aggregated ones, and sorting/paging would follow the hours, not the buckets.
    //
    // labelFn carries the hour ranges the DaypartCard already shows on its axis
    // (#4385). Without it these rows read "Late Fringe" with nothing saying when
    // that is — the same lookup problem #4278 was raised to solve, one card up.
    { key: 'daypart', label: 'Daypart', dims: ['daypart'], labelFn: daypartLabel },
  ],
};

/** First chip of a tab that actually has a backing dimension. */
export const defaultChipKey = (tab: string): string => {
  const chips = CHIPS[tab] ?? [];
  return (chips.find(c => c.dims) ?? chips[0])?.key ?? '';
};

/**
 * Inventory's raw App/Domain chips (flank#4643/#4644) — hidden from the chip
 * row entirely for a user without `can_view_raw_inventory`, per whatever
 * `GET /reporting-2.0/dimensions` actually returns, rather than a second
 * flank-side permission check. `publisher` (the default view) is never
 * gated by this.
 */
const RAW_INVENTORY_CHIP_KEYS = new Set(['app_name', 'domain']);

/** The chips actually offered for a tab, given the caller's available dimensions. */
export const visibleChips = (tab: string, availableDimensions: Set<string>): Chip[] =>
  tab === 'inventory'
    ? (CHIPS.inventory ?? []).filter(c => !RAW_INVENTORY_CHIP_KEYS.has(c.key) || availableDimensions.has(c.key))
    : (CHIPS[tab] ?? []);

const SORTABLE_COLS = [
  'impressions',
  'spend',
  'cpm',
  'clicks',
  'ctr',
  'cpc',
  'reach',
  'frequency',
  'vcr',
  'conversions',
  // Not actually sortable — see COLS' `sortable: false` below. Listed here
  // only so their column type-checks against the shared MetricSortCol union.
  'client_spend',
  'client_cpm',
  'margin',
  // Social engagement (flank#4634) — plain SUMs in metricRegistry.ts, so
  // ordinary server-side sort applies like any other additive metric.
  'link_clicks',
  'post_engagement',
  'video_plays',
  'video_completes',
  'reactions',
  'comments',
  'shares',
] as const;
type MetricSortCol = (typeof SORTABLE_COLS)[number];

/**
 * The name column sorts too. It's a sentinel rather than a real field because
 * the underlying dimension changes with the chip (geo_dma, app_name, product…),
 * and it's resolved to that dimension at query time.
 *
 * Sorting is SERVER-side: the grid is paged, so sorting the fetched page would
 * only order the rows you can already see and silently disagree with page 2.
 * The query layer accepts ORDER BY on any selected dimension.
 */
const NAME_SORT = '__name' as const;
type SortCol = MetricSortCol | typeof NAME_SORT;

/**
 * Grid columns. Reach / Frequency / VCR / CPM were the reference design's and
 * were the largest remaining column gap; all four are on IMPRESSION_FACTS so
 * they resolve for every chip here.
 *
 * They are legitimately SPARSE in canonical today — Reach is absent on GAM
 * (100%), DV360 (98%) and Simpli.fi (99%), and spend is absent on GAM entirely,
 * so CPM and Frequency read "—" for those platforms. Showing the column with a
 * dash is still better than omitting it: the gap becomes visible and
 * attributable instead of looking like the metric was never built.
 *
 * "Distribution" (share of total) is deliberately NOT here — it needs a total
 * the paged endpoint doesn't return, and computing it from the visible page
 * would show a share of 20 rows while claiming to be a share of everything.
 */
export const COLS: {
  key: MetricSortCol;
  label: string;
  format: (v: unknown) => string;
  width: string;
  /** Shown as a header tooltip where the metric's name alone can mislead. */
  note?: string;
  /**
   * Client Spend/CPM (#4446) are computed after the query runs, so the
   * backend rejects sorting by them — the header renders but isn't
   * clickable. Every other column is sortable by default.
   */
  sortable?: boolean;
  /**
   * Metric key this column reads restriction for (`restrictedFields` entries
   * are `metrics.<key>`). Only set on columns that can actually be
   * permission-gated — a restricted column is dropped from `visibleCols`
   * entirely (#4636), rather than rendered with a locked cell (review,
   * flank#4446).
   */
  restrictedMetric?: string;
  /**
   * Suppressed unless every row is one platform SAFE for this specific metric.
   * Reach is an audience count, not a sum — adding it across DSPs counts
   * anyone they share twice, and frequency inherits that error through its
   * denominator. Conversions has the same failure mode but a different set of
   * safe DSPs (REACH_SAFE_DSPS vs CONVERSIONS_SAFE_DSPS in format.ts) — a
   * single shared boolean here would either wrongly hide reach for a DSP
   * that's fine, or wrongly show conversions for one that isn't (review,
   * flank#4479).
   */
  perPlatformMetric?: 'reach' | 'conversions';
  /** Replaces `note` in the header while the column is shown per row (#4604). */
  entityNote?: string;
  /** Replaces `note` in the header while the column is suppressed. */
  gatedNote?: string;
  /**
   * Replaces `gatedNote` for a viewer without canViewPlatform — the default
   * `gatedNote` names DSPs and points at the Platform breakdown, neither of
   * which a restricted viewer can see or use (preprod QA: that leaked DSP
   * identity through the column's tooltip/accessible name).
   */
  gatedNoteRestricted?: string;
  /**
   * Hidden in print only (flank#4310) to free width on the widest
   * breakdown; see the print block in index.css for the actual overflow
   * causes.
   */
  printHide?: boolean;
}[] = [
  // Widths below were rebalanced (flank#4716 follow-up): `num`/`decimal`
  // columns (Impressions, Clicks, Reach) have no compact-magnitude ceiling
  // (see toNumber in format.ts) and were overflowing their old 7-9% columns
  // into the next one on screen — e.g. a Reach of "9,072,585" running
  // straight into the next cell's "1.84" with zero visible gap. Currency/
  // percent columns stay narrower since those formats are always short.
  { key: 'impressions', label: 'Impressions', format: num, width: '10%' },
  { key: 'spend', label: 'Spend', format: compactCurrency, width: '8%', restrictedMetric: 'spend' },
  { key: 'cpm', label: 'CPM', format: currencyPrecise, width: '7%', restrictedMetric: 'cpm' },
  { key: 'clicks', label: 'Clicks', format: num, width: '9%' },
  { key: 'ctr', label: 'CTR', format: percent, width: '7%', printHide: true, note: CTR_CLICKABLE_NOTE },
  // Wholesale spend per click (flank#4018), gated exactly like CPM. printHide
  // for the same reason as CTR/VCR (flank#4310): it's another narrow, sortable
  // column, and #4682 left it out of print's column budget, which overlapped
  // headers/cells on the widest breakdown again.
  { key: 'cpc', label: 'CPC', format: currencyPrecise, width: '7%', restrictedMetric: 'cpc', printHide: true },
  {
    key: 'reach',
    label: 'Reach',
    format: num,
    width: '10%',
    perPlatformMetric: 'reach',
    entityNote: ENTITY_REACH_NOTE,
    gatedNote: REACH_UNAVAILABLE_NOTE,
    gatedNoteRestricted: REACH_UNAVAILABLE_NOTE_RESTRICTED,
  },
  {
    key: 'frequency',
    label: FREQUENCY_LABEL,
    format: decimal,
    width: '9%',
    note: FREQUENCY_NOTE,
    perPlatformMetric: 'reach',
    gatedNote: FREQUENCY_UNAVAILABLE_NOTE,
    gatedNoteRestricted: FREQUENCY_UNAVAILABLE_NOTE_RESTRICTED,
  },
  // Whole percent, no decimals (prep #0000) — same VCR-only decision as the KPI strip.
  { key: 'vcr', label: 'VCR', format: percentWhole, width: '6%', printHide: true },
  {
    key: 'client_spend',
    label: 'Client Spend',
    format: compactCurrency,
    width: '9%',
    sortable: false,
    restrictedMetric: 'client_spend',
  },
  {
    key: 'client_cpm',
    label: 'Client CPM',
    format: currencyPrecise,
    width: '8%',
    sortable: false,
    restrictedMetric: 'client_cpm',
  },
  {
    key: 'margin',
    label: 'Margin',
    format: compactCurrency,
    width: '8%',
    sortable: false,
    // Derived from client_spend, so it must be gated exactly like Client
    // Spend itself — otherwise a restricted role could read the sold CPM
    // back out of the margin figure (#4447).
    restrictedMetric: 'client_spend',
  },
];

/**
 * Social engagement columns (flank#4634) — kept OUT of `COLS` itself, since
 * `COLS` is also used unfiltered by PublisherInventoryTable.tsx and the
 * default preset below; these only belong in the Social preset.
 *
 * Objective, Results and Cost per result are NOT here: Tercept hasn't added
 * an objective field to the Meta/TikTok feed, so CAMPAIGN_OBJECTIVE is NULL
 * on every row in canonical today (Lucas, 2026-10-17). LinkedIn columns are
 * also deliberately absent — no LinkedIn ingestion ticket exists yet, and
 * Tercept hasn't confirmed the field mappings a design mock assumed.
 */
const SOCIAL_ONLY_COLS: typeof COLS = [
  { key: 'link_clicks', label: 'Link clicks', format: num, width: '8%' },
  // TikTok has no POST_ENGAGEMENT column at all (Meta only); NULL there reads
  // "—" via the standard null-cell handling, same as any other sparse metric.
  { key: 'post_engagement', label: 'Post engagement', format: num, width: '9%' },
  { key: 'video_plays', label: 'Video views', format: num, width: '8%' },
  { key: 'video_completes', label: 'Completed views', format: num, width: '9%' },
  // Shares/Reactions/Comments only have real data on TikTok, and only from
  // 2026-10-02 (no backfill before it) — Meta's columns exist but are NULL.
  {
    key: 'shares',
    label: 'Shares',
    format: num,
    width: '7%',
    note: 'TikTok only. Data starts Oct 2, 2026 — no backfill before it.',
  },
  {
    key: 'reactions',
    label: 'Reactions',
    format: num,
    width: '8%',
    note: 'TikTok only. Data starts Oct 2, 2026 — no backfill before it.',
  },
  {
    key: 'comments',
    label: 'Comments',
    format: num,
    width: '8%',
    note: 'TikTok only. Data starts Oct 2, 2026 — no backfill before it.',
  },
];

/**
 * Meta and TikTok — the only platforms with real social data in canonical
 * today (flank#4634). LinkedIn is deliberately excluded: no ingestion ticket
 * exists yet and Tercept hasn't confirmed a design mock's field mappings for
 * it, so building to them would be guessing at data that may not match.
 */
const SOCIAL_DSPS = ['metaads', 'tiktok'];

/** True once every selected platform is a social one — never on an empty filter. */
export const isSocialOnlyFilter = (dsps: string[]): boolean =>
  dsps.length > 0 && dsps.every(dsp => SOCIAL_DSPS.includes(dsp));

/**
 * The Social preset's column set (flank#4634) — Impressions/Spend/CPM/Clicks/
 * CTR/CPC/Reach/Frequency plus every SOCIAL_ONLY_COLS entry. VCR and Margin
 * are left out, matching the reference mock (VCR is a programmatic-video
 * metric; Margin isn't part of the social-performance read). Client Spend/CPM
 * stay in so `visibleColumns`'s existing hideClientMoney/permission gating
 * still applies exactly as it does for the default preset.
 */
const SOCIAL_PRESET_KEYS = new Set<MetricSortCol>([
  'impressions',
  'spend',
  'cpm',
  'clicks',
  'ctr',
  'cpc',
  'reach',
  'frequency',
  'client_spend',
  'client_cpm',
  ...SOCIAL_ONLY_COLS.map(col => col.key),
]);

/**
 * Whether a column is suppressed under the current scopes. Reach and conversions
 * have different verified-safe DSP sets, so they gate independently. Defined once:
 * the header, the cells and the sort reset must agree, and three copies would drift.
 */
/**
 * Columns dropped from the table entirely for this result, rather than shown
 * empty (#4446, agreed with Martin 2026-09-24).
 *
 * Client Spend and Client CPM come from the sold CPM on a ribeye line item, so
 * only Flank-activated (Beeswax) delivery can carry them — every other platform
 * has no line item to hang a rate on. Rather than keep a platform list here that
 * drifts as platforms are added, this reads the result: if nothing in view
 * resolved a figure, the columns are not offered.
 *
 * The API omits the key entirely (it does not send null) when a group is
 * incomplete, so `in` is the honest test — `!= null` would also be true of a
 * legitimate zero.
 */
const CLIENT_MONEY_KEYS = new Set(['client_spend', 'client_cpm', 'margin']);

/**
 * Total row columns that can't be added across rows (flank#4645) — the same
 * person/conversion can be counted on more than one row, so the Total shows
 * "Not additive" instead of a summed number. See buildTotalRowValues in
 * format.ts, which leaves these three out of its computed values entirely.
 */
const NOT_ADDITIVE_KEYS: Record<string, string> = {
  reach: REACH_NOT_ADDITIVE_NOTE,
  frequency: REACH_NOT_ADDITIVE_NOTE,
};

/**
 * #4442/#4636 — Spend/CPM fall back to the role default (see
 * useReportingPermissions) since there's no per-user override yet; every
 * restrictedMetric column (including Client Spend/CPM/Margin, which DOES
 * have a real permission already) is omitted outright rather than rendered
 * locked. Exported standalone so the omission rule is unit-testable without
 * mounting the table (see visibleColumns.test.ts).
 */
export const visibleColumns = (
  cols: typeof COLS,
  hideClientMoney: boolean,
  canViewWholesaleSpend: boolean,
  restrictedFields: string[],
): typeof COLS =>
  cols.filter(col => {
    if (hideClientMoney && CLIENT_MONEY_KEYS.has(col.key)) return false;
    // Margin = client_spend − spend, so it needs the same wholesale gate as
    // spend/cpm — otherwise a viewer with Client Spend but not wholesale
    // Spend could back wholesale spend out of the rendered margin figure
    // (same fix as GroupedBreakdownCard's visibleGroupedMetrics, flank#4650).
    // CPC is wholesale spend per click, so it carries the same gate (flank#4018).
    if (
      (col.key === 'spend' || col.key === 'cpm' || col.key === 'cpc' || col.key === 'margin') &&
      !canViewWholesaleSpend
    ) {
      return false;
    }
    return !col.restrictedMetric || !restrictedFields.includes(`metrics.${col.restrictedMetric}`);
  });

export const isGated = (
  col: (typeof COLS)[number],
  perPlatformReach: boolean,
  perPlatformConversions: boolean,
): boolean =>
  col.perPlatformMetric === 'reach'
    ? !perPlatformReach
    : col.perPlatformMetric === 'conversions'
      ? !perPlatformConversions
      : false;

/**
 * Whether a column's header can be clicked to sort. A suppressed column can't —
 * and neither can Reach/Frequency on a DSP-entity breakdown (#4604): sorting is
 * server-side, so it would rank rows by the figures reachRowNote hides on GAM /
 * DV360 / unprefixed rows, putting "—" cells at the top (review, flank#4605).
 */
export const isSortGated = (
  col: (typeof COLS)[number],
  reachColumn: boolean,
  perPlatformConversions: boolean,
  entityReach: boolean,
): boolean => isGated(col, reachColumn, perPlatformConversions) || (entityReach && col.perPlatformMetric === 'reach');

/**
 * The header tooltip for a column: why it's suppressed, else how to read it per
 * row on a DSP-entity breakdown (#4604), else its ordinary note. The "can't be
 * combined across platforms" wording only belongs where rows can span them.
 */
export const columnNote = (
  col: (typeof COLS)[number],
  gated: boolean,
  entityReach: boolean,
  canViewPlatform: boolean,
): string | undefined =>
  gated
    ? canViewPlatform
      ? col.gatedNote
      : (col.gatedNoteRestricted ?? col.gatedNote)
    : entityReach && col.entityNote
      ? col.entityNote
      : col.note;

export type ResolvedDims = {
  dims: string[];
  label: string;
  idKey?: string;
  unavailable?: string;
  labelFn: (value: unknown, id?: unknown) => string;
  showAdvertiser?: boolean;
};

/**
 * The active chip's query shape. Falls back to the tab's first usable chip.
 * `chips` defaults to the tab's full static list; Inventory passes its
 * permission-filtered subset (see `visibleChips`) so a stale `app_name`/
 * `domain` selection a disallowed user can no longer click can't still
 * resolve to one. Exported for the Conversions widget (flank#4648) — same
 * tab/chip navigation, a different metric set.
 */
export const resolve = (tab: string, chipKey: string, chips: Chip[] = CHIPS[tab] ?? CHIPS.channel): ResolvedDims => {
  const chip = chips.find(c => c.key === chipKey) ?? chips.find(c => c.dims) ?? chips[0];
  if (!chip?.dims) {
    return {
      dims: [],
      label: '',
      // No rows render in this state; the field is required, so name the
      // pass-through rather than leaving a hole.
      labelFn: dimensionLabel,
      unavailable: chip?.reason ?? 'This breakdown is not available yet.',
    };
  }
  return {
    dims: chip.dims,
    // Single-dimension chips label by the dimension itself; id/name pairs carry
    // an explicit labelKey.
    label: chip.labelKey ?? chip.dims[chip.dims.length - 1],
    idKey: chip.idKey,
    labelFn: chip.labelFn,
    showAdvertiser: chip.showAdvertiser,
  };
};

/** Shorten long DSP IDs for the inline disambiguator (full value stays in the tooltip). */
const shortEntityId = (value: unknown): string => {
  const s = String(value ?? '').trim();
  if (!s) return '';
  return s.length > 12 ? `…${s.slice(-8)}` : s;
};

const SortIcon = ({ col, sortCol, sortDir }: { col: SortCol; sortCol: SortCol; sortDir: 'asc' | 'desc' }) => {
  if (col !== sortCol)
    return <UnfoldMoreRounded sx={{ fontSize: 14, color: COLORS.border, verticalAlign: 'middle' }} />;
  return sortDir === 'desc' ? (
    <ArrowDownwardRounded sx={{ fontSize: 14, color: COLORS.primary, verticalAlign: 'middle' }} />
  ) : (
    <ArrowUpwardRounded sx={{ fontSize: 14, color: COLORS.primary, verticalAlign: 'middle' }} />
  );
};

const ELLIPSIS = -1;

/**
 * Page numbers to show: always the first and last, plus a window around the
 * current page, with gaps elided. A 500-page result would otherwise render 500
 * buttons; jumping to the end is the one long-range move worth keeping.
 */
export const pageWindow = (page: number, pageCount: number): number[] => {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i);

  const out = new Set<number>([0, pageCount - 1, page]);
  for (const n of [page - 1, page + 1]) if (n > 0 && n < pageCount - 1) out.add(n);

  const sorted = [...out].sort((a, b) => a - b);
  const withGaps: number[] = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) withGaps.push(ELLIPSIS);
    withGaps.push(n);
  });
  return withGaps;
};

/** Row counts the selector offers (prep #0000 — same 20/50/100 decision as
 * Conversions.tsx). Duplicated rather than imported from there to avoid a
 * cross-file import cycle — Conversions.tsx already imports `pageWindow` from
 * this file. */
export const ROWS_PER_PAGE_OPTIONS = [20, 50, 100] as const;

/**
 * Page count for the server-paged grid, given the server's reported
 * `totalRows` and the viewer's current Rows selection. Unlike Conversions.tsx's
 * client-side `paginateRows`, this grid pages via limit/offset on the request
 * itself (`rows` only ever holds the current page), so there is nothing to
 * slice here — just the count and whether a next page exists. Exported
 * standalone so switching the Rows selector's effect on paging is
 * unit-testable without mounting the widget.
 */
export const paginationInfo = (
  totalRows: number,
  page: number,
  rowsPerPage: number,
): { pageCount: number; hasNextPage: boolean } => {
  const pageCount = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  return { pageCount, hasNextPage: page + 1 < pageCount };
};

/**
 * Whether the Creative Library's preview modal can show more than the thumbnail:
 * file-backed types need the asset, tag-backed types need the tag.
 */
const canOpenPreview = (preview: CreativePreview | undefined): preview is CreativePreview => {
  switch (preview?.type) {
    case CreativeType.VIDEO:
    case CreativeType.DISPLAY:
    case CreativeType.AUDIO:
      return Boolean(preview.file_url);
    case CreativeType.VIDEO_VAST:
    case CreativeType.AUDIO_VAST:
    case CreativeType.DISPLAY_TAG:
      return Boolean(preview.tag_content);
    default:
      return false;
  }
};

const ImpressionsByType = () => {
  const dispatch = useAppDispatch();
  const [tab, setTab] = useState('hierarchy');
  const [subTab, setSubTab] = useState('campaign');
  const { widths: colWidths, startResize } = useResizableColumns(`impressionsByType.${tab}`);
  const [sortCol, setSortCol] = useState<SortCol>('impressions');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE_OPTIONS[0]);
  // flank#4634 — the Social preset auto-switches on when the platform filter
  // narrows to only social DSPs, but a manual toggle can override it. `null`
  // means "follow auto"; set back to null whenever auto's own answer changes,
  // so a stale override doesn't survive the user picking a different filter.
  const [manualPreset, setManualPreset] = useState<'default' | 'social' | null>(null);

  // #4643 — Inventory's raw App/Domain chips are hidden entirely for a user
  // without #4644's can_view_raw_inventory, per whatever the dimensions()
  // endpoint actually returns (see visibleChips); Publishers is never gated.
  const availableDimensions = useAvailableDimensions();
  // The Platform chip (dims: ['dsp']) must stay gated the same as every other
  // Platform surface (review, flank#4650) — filtered out here too, AND
  // falling back off an already-selected 'dsp' chip, so `resolve` can never
  // be asked to request the restricted dimension.
  const { canViewPlatform, canViewWholesaleSpend, hasClientSpendData } = useReportingPermissions();
  const chipsForTab = visibleChips(tab, availableDimensions).filter(
    chip => canViewPlatform || !chip.dims?.includes('dsp'),
  );
  const activeSubTab = chipsForTab.some(chip => chip.key === subTab) ? subTab : defaultChipKey(tab);
  const isPublisherView = tab === 'inventory' && activeSubTab === 'publisher';
  // ExportDialog lives outside this component and reads the generic
  // 'impressionsByType' widget from the global store — which sits stale or
  // unfetched while this chip is active (see the widget's own `enabled`
  // below). Told about it here so it can exclude the section rather than
  // silently export the wrong chip's rows (Lucas, review).
  useEffect(() => {
    dispatch(setPublisherInventoryActive(isPublisherView));
    return () => {
      dispatch(setPublisherInventoryActive(false));
    };
  }, [dispatch, isPublisherView]);

  const {
    dims,
    label,
    idKey,
    unavailable,
    labelFn: resolvedLabelFn,
    showAdvertiser,
  } = resolve(tab, activeSubTab, chipsForTab);
  // GAM's channel taxonomy bakes the platform name into the value itself
  // ("GAM Display"/"GAM Video") where every other DSP's same channel type
  // reads bare — a second DSP-identifying leak alongside the Platform chip
  // (flank#4741 QA) for a user without canViewPlatform. maskedChannelLabelFn
  // only touches the Channel chip; every other chip's values don't encode a
  // platform name. Shared with Conversions.tsx, which resolves the same chip
  // through the same `resolve`/CHIPS helper.
  const labelFn = maskedChannelLabelFn(activeSubTab, canViewPlatform, resolvedLabelFn);
  const filters = useAppSelector(selectReportingV2Filters);
  // Reach/frequency and conversions are only safe where a row can't span DSPs
  // — see isPerPlatformScope — but not the SAME set of DSPs is safe for both
  // (REACH_SAFE_DSPS vs CONVERSIONS_SAFE_DSPS in format.ts), so each metric
  // gets its own gate rather than one shared boolean (review, flank#4479).
  // Suppressed elsewhere rather than summed.
  const perPlatformReach = isPerPlatformScope(filters.dsps, dims);
  // A DSP-entity breakdown (Advertiser, Campaign, Line Item, Creative) is one
  // platform per ROW even when the table spans several, so its reach columns
  // stay available and each row decides for itself — see reachRowNote (#4604).
  const entityReach = !perPlatformReach && isDspEntityScope(dims);
  // DV360-only Campaign/Device breakdowns read reach live from DV360 per row (flank#4479).
  const liveBreakdown = activeSubTab === 'campaign' || activeSubTab === 'device' ? activeSubTab : null;
  const liveReach = useDv360LiveReach(liveBreakdown ?? 'total', { enabled: liveBreakdown !== null });
  const reachColumn = perPlatformReach || entityReach || liveReach.active;
  // Server-side sort would rank rows by the summed reach the live values replace.
  const reachSortGated = entityReach || liveReach.active;
  const perPlatformConversions = isConversionsPerPlatformScope(filters.dsps, dims);
  // A DSP-entity breakdown is also safe to show conversions on (same reasoning
  // as entityReach above), so this is the wider of the two gates.
  const conversionsColumn = isConversionsColumnShown(filters.dsps, dims);

  // flank#4634 — auto-detect, with the manual toggle (below) able to override it.
  const autoPreset = isSocialOnlyFilter(filters.dsps) ? 'social' : 'default';
  const preset = manualPreset ?? autoPreset;
  // A manual override belongs to the filter state that produced it — carrying
  // it across a genuinely different filter selection would strand the grid on
  // "Default" after the user narrows to Social from a GAM-only override, or
  // vice versa.
  useEffect(() => {
    setManualPreset(null);
  }, [autoPreset]);

  // Widening the filter must not strand the table on a per-platform-only
  // choice. Sorting is SERVER-side, so a sortCol left on a suppressed column
  // keeps the paged query ORDER BYing the cross-platform sum we are refusing
  // to display — rows ranked by a number that reads "—" in every cell.
  useEffect(() => {
    const sorted = COLS.find(c => c.key === sortCol);
    if (sorted && isSortGated(sorted, reachColumn, conversionsColumn, reachSortGated)) {
      // handleSort resets the page on every manual sort change; this
      // programmatic reset has to match, or the ordering changes under a
      // page number that no longer points at the same rows (review, #4477).
      setSortCol('impressions');
      setPage(0);
    }
  }, [reachColumn, perPlatformConversions, conversionsColumn, reachSortGated, sortCol]);
  // The name sentinel resolves to whichever dimension this chip labels by.
  const sortField = sortCol === NAME_SORT ? label : sortCol;
  const {
    rows: reportRows,
    totalRows,
    loading,
    error,
    restrictedFields,
    ignoredFilters,
  } = useReportWidget('impressionsByType', {
    dimensions: withEntityDsp(dims, filters.dsps),
    // client_spend/client_cpm (#4446) — permission-gated and Beeswax-only
    // server-side; the request just asks for them like any other metric.
    // platform_count backs isAdvertiserRollupNotAdditive below; single_dsp
    // backs reachRowNote's single-platform-rollup case (review, Lucas) —
    // harmless to ask for on every chip, like client_spend/client_cpm already are.
    metrics: [
      'impressions',
      'spend',
      'cpm',
      'clicks',
      'ctr',
      'cpc',
      'reach',
      'frequency',
      'vcr',
      'client_spend',
      'client_cpm',
      'platform_count',
      'single_dsp',
      // Social engagement (flank#4634) — PERFORMANCE-only metrics (review,
      // Lucas), so requesting them alongside a PERFORMANCE-incompatible
      // dimension (Inventory's app_name/domain) breaks the query outright.
      // Unlike client_spend/client_cpm, these can't be "always fetch, gate
      // client-side" — only ask for them when the Social preset is actually
      // showing them, which means a refetch on toggle, same as any other
      // preset-driven metric set would.
      ...(preset === 'social'
        ? ([
            'link_clicks',
            'post_engagement',
            'video_plays',
            'video_completes',
            'reactions',
            'comments',
            'shares',
          ] as const)
        : []),
    ],
    sort: sortField ? [{ field: sortField, dir: sortDir }] : undefined,
    limit: rowsPerPage,
    offset: page * rowsPerPage,
    // Don't query a chip with no backing dimension — it would 4xx. Publishers
    // owns its own pair of fetches (usePublisherInventory) instead of this
    // generic one, which would otherwise ask for raw app_name rows no
    // Publishers viewer needs rendered one-per-row.
    enabled: !unavailable && !isPublisherView,
  });
  const rows = useMemo(
    () =>
      liveReach.active && liveBreakdown
        ? reportRows.map((row): ReportRow => {
            const live = liveReach.byKey.get(liveReachKey(liveBreakdown, row[liveBreakdown]));
            const covered = liveCovers(row.impressions, live);
            return {
              ...row,
              reach: covered ? (live?.reach ?? null) : null,
              frequency: covered ? (live?.frequency ?? null) : null,
            };
          })
        : reportRows,
    [reportRows, liveReach.active, liveReach.byKey, liveBreakdown],
  );
  // The page-level Export dialog reads rows from the store; give it the same live rows the grid shows.
  useEffect(() => {
    dispatch(
      setImpressionsByTypeLive({
        rows: liveReach.active && !liveReach.loading ? rows : null,
        loading: liveReach.active && liveReach.loading,
      }),
    );
  }, [dispatch, rows, liveReach.active, liveReach.loading]);
  useEffect(() => () => void dispatch(setImpressionsByTypeLive({ rows: null, loading: false })), [dispatch]);
  // #4446: drop the client-money columns rather than render a full column of
  // "—". They only resolve for Beeswax, so on any other platform the header
  // would promise a figure the row can never carry.
  // flank#4745 follow-up (preprod QA): clientMoneyUnavailable alone only catches a
  // row shape with no client_spend/client_cpm KEY at all — it missed the
  // Bonneville-style case where the key is present but every value is
  // unpriced for the current selection. hasClientSpendData is the same
  // priced-impressions signal TopCampaignsCard/TopPublishersCard already use.
  const hideClientMoney = clientMoneyUnavailable(rows) || !hasClientSpendData;
  // flank#4634 — the Social preset swaps which COLS entries are even offered
  // to visibleColumns; permission/client-money gating then applies exactly as
  // it does for the default preset, unchanged.
  const presetCols =
    preset === 'social' ? [...COLS, ...SOCIAL_ONLY_COLS].filter(col => SOCIAL_PRESET_KEYS.has(col.key)) : COLS;
  const visibleCols = visibleColumns(presetCols, hideClientMoney, canViewWholesaleSpend, restrictedFields);

  // appDetails (the expanded row's per-bundle/DSP breakdown) is itself the raw,
  // gated app_name dimension — only worth fetching when the viewer could ever
  // see it, same canViewPlatform gate PublisherInventoryTable already renders it behind.
  const publisherInventory = usePublisherInventory(isPublisherView, canViewPlatform);

  // Total row (flank#4645): reads the SAME ungrouped 'totals' widget KpiStrip
  // reads, rather than summing this grid's own (paged) rows — so it matches
  // the KPI cards by construction and covers every row matching the current
  // filters, not just the page on screen. One global aggregate serves every
  // tab/chip here, since none of them filter the data, only how it's grouped.
  const totalsWidget = useAppSelector(selectReportingV2Widget('totals'));
  const totalRowValues = useMemo(() => buildTotalRowValues(totalsWidget.rows[0]), [totalsWidget.rows]);
  const totalRowMismatch = isTotalRowMismatched(dims);
  // Which explanation applies — APP/DOMAIN source table vs NULL-hour exclusion
  // (flank#4837) — depends on which dim tripped `totalRowMismatch` above.
  const totalRowMismatchNoteText = totalRowMismatchNote(dims);
  // flank#4880 — every Geo chip here routes to the same GEOGRAPHY table the
  // map does, so it drops Device/Publisher filters the same silent way; this
  // tab was missed when the map got the disclosure (review, Lucas). Gated on
  // the Geo tab specifically, not on `dims`, since GEOGRAPHY's own ignored
  // keys never show up on a non-geo chip's response in the first place.
  const geoIgnoredFilterNoteText = tab === 'geo' ? geoIgnoredFilterNote(ignoredFilters) : null;
  // CodeRabbit (flank#4649): the grid can finish loading before this
  // independent 'totals' request does. Showing `Total · N rows` with blank
  // values would present an incomplete aggregate as a completed one.
  const totalsReady = Boolean(totalsWidget.reqId) && !totalsWidget.loading && !totalsWidget.error;

  const { pageCount, hasNextPage } = paginationInfo(totalRows, page, rowsPerPage);

  // A dimension can exist in canonical and still be entirely NULL for the current
  // selection: GAM carries no geo_zip or geo_dma at all, so ZIP under a GAM-only
  // view collapsed to a single "Unnamed item" row holding every impression, which
  // reads as broken data rather than a gap in the feed. `unavailable` above only
  // covers dimensions that are permanently absent (County); this is the runtime
  // case. GeographicPerformance already does the same thing for its chips.
  const allUnmapped = rows.length > 0 && rows.every(row => dimensionLabel(row[label]) === 'Unmapped');

  /**
   * Drop an unmapped row carrying no data at all — that's the absence of a value
   * rendered as a category. A *named* row of zeroes stays: "Tablet: 0" is an answer.
   *
   * On the Geo tab the placeholders also get dropped when they DO carry data,
   * because there they aren't a long tail — they're the top of the table. A
   * 30-day ZIP breakdown opened with GAM's nulls (135M impressions; GAM emits no
   * ZIP at all) and DV360's literal "Unknown" (25M) above a largest real ZIP of
   * 801K. Ranking a place against "no place" isn't a comparison, so the geo
   * placeholders move to the note below the table instead.
   */
  const isGeoTab = tab === 'geo';
  const visibleRows = useMemo(
    () =>
      rows.filter(row => {
        if (isGeoTab && isPlaceholderDimension(row[label])) return false;
        if (dimensionLabel(row[label]) !== 'Unmapped') return true;
        return SORTABLE_COLS.some(metric => Number(row[metric] ?? 0) !== 0);
      }),
    [rows, label, isGeoTab],
  );
  const rowsWithMargin = useMemo(() => withMargin(visibleRows), [visibleRows]);

  // The Total row's CSV shape: the same computed values the on-screen footer
  // shows, labelled on whichever column this chip names (campaign_name,
  // geo_dma, …) since that's the only column guaranteed to read as "this is
  // the Total" regardless of which tab/chip produced these rows.
  const csvTotalRow = useMemo(
    () =>
      visibleRows.length === 0 || !totalsReady
        ? undefined
        : {
            ...totalRowValues,
            [label]: `Total · ${totalRows.toLocaleString()} rows`,
            // Gated the same way the on-screen Total row is (review, CodeRabbit):
            // a gated Reach/Frequency/Conversions column reads "—" on screen, so
            // the export must agree instead of always claiming "Not additive".
            ...Object.fromEntries(
              Object.keys(NOT_ADDITIVE_KEYS).map(key => [
                key,
                (key === 'conversions' ? !conversionsColumn : !reachColumn) ? '—' : NOT_ADDITIVE_LABEL,
              ]),
            ),
          },
    [totalRowValues, label, totalRows, visibleRows.length, totalsReady, reachColumn, conversionsColumn],
  );
  const onExportCsv = useExportWidgetCsv(
    'impressionsByType',
    rows,
    totalRows,
    csvTotalRow,
    restrictedFields.includes('metrics.client_spend'),
    // Paired with `totalsReady` (review, Lucas/CodeRabbit, flank#4683): same
    // gap as ExportDialog.tsx had — a failed totals request must not flag a
    // mismatch note for a Total row that `csvTotalRow` never appended.
    totalRowMismatch && totalsReady,
    liveReach.active,
  );

  // Hiding rows silently would leave the table implying it shows all delivery.
  // Disclose what moved out, matching BreakdownCard and GeographicPerformance.
  const hiddenImpressions = useMemo(
    () => (isGeoTab ? sumOf(rows, 'impressions') - sumOf(visibleRows, 'impressions') : 0),
    [isGeoTab, rows, visibleRows],
  );

  // Same cleaned display name can map to multiple DSP entity IDs — flag those
  // rows (and blank-name rows) so they don't look like accidental duplicates.
  const ambiguousNames = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of rows) {
      const name = labelFn(row[label], idKey ? row[idKey] : undefined);
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([name]) => name));
  }, [rows, label, idKey, labelFn]);

  /**
   * An entity-tier breakdown always carries `dsp` alongside its own id
   * (withEntityDsp, so Reach/Frequency/VCR are never summed across
   * platforms) — a linked advertiser delivering on two platforms is two
   * rows with the SAME name and the SAME id, just a different platform.
   * `ambiguousNames`'s id sub-label can't tell those apart: showing "3287"
   * under both reads as a duplicated row, not two genuine ones (Josh,
   * preprod demo-prep review). Flag that specific case so the row falls
   * back to naming the platform instead of repeating the id.
   */
  const ambiguousEntityIds = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of rows) {
      const name = labelFn(row[label], idKey ? row[idKey] : undefined);
      const id = idKey ? String(row[idKey] ?? '').trim() : '';
      counts.set(`${name}\u0000${id}`, (counts.get(`${name}\u0000${id}`) ?? 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([key]) => key));
  }, [rows, label, idKey, labelFn]);

  /**
   * Creative artwork for the rows on screen (FB-004).
   *
   * Only fetched on the creative breakdown, and only for the ids actually
   * rendered — a report can carry thousands of creatives and the previews are
   * looked up one row at a time server-side.
   *
   * Coverage is Beeswax and Simpli.fi only, so an unresolved id shows no
   * thumbnail rather than a broken image.
   */
  const [previews, setPreviews] = useState<Map<string, CreativePreview>>(new Map());
  const [openPreview, setOpenPreview] = useState<CreativePreview | null>(null);

  useEffect(() => {
    if (idKey !== 'creative') {
      setPreviews(new Map());
      return;
    }
    const ids = [...new Set(rows.map(row => String(row[idKey] ?? '').trim()).filter(Boolean))];
    if (ids.length === 0) return;

    const controller = new AbortController();
    fetchCreativePreviews(ids, controller.signal)
      .then(setPreviews)
      .catch(error => {
        // An abort is this effect cleaning up after itself, not a failure —
        // clearing on it would wipe the previews for the request that replaced
        // it, and only rejection ordering was hiding that.
        if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') return;
        // A missing preview must never take the table down with it.
        setPreviews(new Map());
      });
    return () => controller.abort();
  }, [rows, idKey]);

  const handleSort = (col: SortCol) => {
    if (col === sortCol) {
      setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortCol(col);
      // Names read naturally A-Z; metrics are asked about largest-first.
      setSortDir(col === NAME_SORT ? 'asc' : 'desc');
    }
    // Re-sorting reorders the whole result set, so page 3 of the old order is
    // meaningless under the new one.
    setPage(0);
  };

  const handleTabChange = (next: string) => {
    setTab(next);
    // Every tab has its own chips now, so carrying the old selection across
    // would resolve to nothing.
    setSubTab(defaultChipKey(next));
    setPage(0);
  };

  const handleSubTabChange = (next: string) => {
    setSubTab(next);
    setPage(0);
  };

  const handleRowsPerPageChange = (next: number) => {
    setRowsPerPage(next);
    setPage(0);
  };

  const cell = (value: unknown, fmt: (v: unknown) => string) =>
    value === null || value === undefined || value === '' ? '—' : fmt(value);

  const tabLabel = TABS.find(t => t.key === tab)?.label ?? 'Name';

  return (
    <Box className='rv2-card' sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Typography variant='subtitle1' sx={{ fontWeight: 600, color: COLORS.text }}>
          Impressions by Type
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* flank#4634 — only offered when the filter is social-only: there's
              nothing to toggle back from otherwise. autoPreset, not `preset`,
              decides whether it renders, so switching the filter away hides
              the toggle rather than leaving the manual override stranded on screen. */}
          {autoPreset === 'social' && (
            <ToggleButtonGroup
              size='small'
              exclusive
              value={preset}
              onChange={(_e, v) => v && setManualPreset(v)}
              sx={darkToggleSx}
            >
              <ToggleButton value='social'>Social</ToggleButton>
              <ToggleButton value='default'>Default columns</ToggleButton>
            </ToggleButtonGroup>
          )}
          {/* While a request is in flight the store still holds the PREVIOUS rows, but the
              export reads the CURRENT filters — gating those rows by a platform filter
              they weren't fetched under (review, flank#4605). Wait for them to settle.
              Publishers owns its own export button instead (different row shape — a
              rollup, not the raw per-dimension rows this one reads). */}
          {!isPublisherView && (
            <WidgetExportButton onExport={onExportCsv} disabled={loading || liveReach.loading || rows.length === 0} />
          )}
        </Box>
      </Box>

      <ToggleButtonGroup
        size='small'
        exclusive
        value={tab}
        onChange={(_e, v) => v && handleTabChange(v)}
        sx={darkToggleSx}
      >
        {TABS.map(t => (
          <ToggleButton key={t.key} value={t.key}>
            {t.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {/* Chips for the active tab. Every tab has them now, not just Hierarchy. */}
      <ToggleButtonGroup
        size='small'
        exclusive
        value={activeSubTab}
        onChange={(_e, v) => v && handleSubTabChange(v)}
        sx={darkToggleSx}
      >
        {chipsForTab.map(chip =>
          chip.dims ? (
            <ToggleButton key={chip.key} value={chip.key}>
              {chip.label}
            </ToggleButton>
          ) : (
            // Disabled rather than hidden, with the reason on hover — the gap
            // should read as "not available yet", not as a missing feature.
            <Tooltip key={chip.key} title={chip.reason ?? ''} placement='top' arrow>
              <span style={{ display: 'inline-flex' }}>
                <ToggleButton value={chip.key} disabled>
                  {chip.label}
                </ToggleButton>
              </span>
            </Tooltip>
          ),
        )}
      </ToggleButtonGroup>

      {unavailable ? (
        <Typography variant='body2' sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
          {unavailable}
        </Typography>
      ) : isPublisherView ? (
        <PublisherInventoryTable
          {...publisherInventory}
          canViewPlatform={canViewPlatform}
          canViewRawInventory={availableDimensions.has('app_name')}
          canViewWholesaleSpend={canViewWholesaleSpend}
          hasClientSpendData={hasClientSpendData}
          entityReach={entityReach}
          reachColumn={reachColumn}
          perPlatformConversions={perPlatformConversions}
        />
      ) : loading ? (
        <WidgetSkeleton rows={20} />
      ) : error ? (
        <Typography variant='body2' color='error'>
          {error}
        </Typography>
      ) : visibleRows.length === 0 ? (
        <Typography variant='body2' sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
          No rows for the selected filters.
        </Typography>
      ) : allUnmapped ? (
        <Typography variant='body2' sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
          No data for this breakdown — the platforms in the current selection don’t report it. Try a different
          breakdown, or narrow to a platform that does.
        </Typography>
      ) : (
        <>
          <Box className='rv2-scroll' sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ tableLayout: 'fixed', minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      // Was 24%/36% — disproportionately wide next to the
                      // numeric columns it was starving (flank#4643 QA
                      // follow-up to #4716); the name keeps its own
                      // ellipsis+tooltip in the body below.
                      width: showAdvertiser ? '14%' : '20%',
                      color: COLORS.muted,
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': { color: COLORS.text },
                    }}
                    onClick={() => handleSort(NAME_SORT)}
                  >
                    {tabLabel} <SortIcon col={NAME_SORT} sortCol={sortCol} sortDir={sortDir} />
                  </TableCell>
                  {showAdvertiser && (
                    <TableCell sx={{ width: '18%', color: COLORS.muted, fontWeight: 600, fontSize: 12 }}>
                      Advertiser
                    </TableCell>
                  )}
                  {visibleCols.map(col => {
                    // `conversionsColumn`, not `perPlatformConversions` (review,
                    // CodeRabbit): a Hierarchy entity breakdown without a
                    // single-platform filter has conversionsColumn true and
                    // perPlatformConversions false — the Conversions column
                    // stays visible, but the header/body cells were still gating
                    // on the wrong flag, same bug as the Total cell below.
                    const gated = isGated(col, reachColumn, conversionsColumn);
                    const unclickable =
                      col.sortable === false || isSortGated(col, reachColumn, conversionsColumn, reachSortGated);
                    const liveCol = liveReach.active && col.perPlatformMetric === 'reach';
                    const note = liveCol ? liveReach.notes.note : columnNote(col, gated, entityReach, canViewPlatform);
                    // Live values are deduplicated over the whole period, not averaged per day.
                    const colLabel = liveCol && col.key === 'frequency' ? 'Frequency' : col.label;
                    return (
                      <TableCell
                        key={col.key}
                        align='right'
                        // Hides printHide columns (CTR/VCR/CPC, flank#4310/#4682) in print only — see COLS' printHide doc.
                        className={col.printHide ? 'rv2-print-hide-col' : undefined}
                        // Full label on hover (Lucas, #4716 review nit) — the
                        // column is often clipped to 2-4 characters by the
                        // ellipsis below with no other way to read the whole name.
                        title={colLabel}
                        sx={{
                          position: 'relative',
                          width: colWidths[col.key] ? `${colWidths[col.key]}px` : col.width,
                          color: COLORS.muted,
                          fontWeight: 600,
                          fontSize: 12,
                          cursor: unclickable ? 'default' : 'pointer',
                          userSelect: 'none',
                          // Two-word labels ("Attr. Convs.") otherwise wrap and push
                          // the sort caret onto a third line, leaving one ragged
                          // header among single-line neighbours.
                          whiteSpace: 'nowrap',
                          // A right-aligned nowrap label wider than its fixed-%
                          // column (e.g. "Avg. Daily Freq.", "Client CPM")
                          // otherwise overflows leftward into the PRECEDING
                          // column's header instead of clipping at its own
                          // boundary — reported as overlapping headers on the
                          // Publishers tab (flank#4643 QA); same COLS/widths
                          // here, so the same fix applies on screen.
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          ...(unclickable ? {} : { '&:hover': { color: COLORS.text } }),
                        }}
                        // Sorting a suppressed column would order the rows by the
                        // cross-platform sum we are refusing to show, while every
                        // cell reads "—". Nothing to sort, so it isn't offered.
                        onClick={unclickable ? undefined : () => handleSort(col.key)}
                      >
                        {note ? (
                          <Tooltip title={note} placement='top' arrow>
                            <span>{colLabel}</span>
                          </Tooltip>
                        ) : (
                          colLabel
                        )}{' '}
                        {unclickable ? null : <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />}
                        <ColumnResizeHandle columnKey={col.key} onResizeStart={startResize} />
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsWithMargin.map((row, index) => {
                  // Chips that pass entityLabel fold the id in for unnamed rows, so
                  // a blank name reads "Unnamed item (GAM0)" rather than "— GAM0".
                  const displayName = labelFn(row[label], idKey ? row[idKey] : undefined);
                  const entityId = idKey ? String(row[idKey] ?? '').trim() : '';
                  const preview = previews.get(entityId);
                  const openable = canOpenPreview(preview);
                  const needsDisambiguator =
                    Boolean(entityId) && (displayName === '—' || ambiguousNames.has(displayName));
                  // The id itself doesn't disambiguate a linked advertiser that
                  // delivers on two platforms (same name, same id) — fall back to
                  // naming the platform, the thing that's actually different.
                  const idAlsoAmbiguous =
                    needsDisambiguator && ambiguousEntityIds.has(`${displayName}\u0000${entityId}`);
                  const platformLabel =
                    idAlsoAmbiguous && typeof row.dsp === 'string' && row.dsp ? dspLabel(row.dsp) : null;
                  const disambiguatorText = platformLabel ?? shortEntityId(entityId);
                  // Same GAM-prefix mask as displayName above — built straight
                  // from the raw row value, so without this the tooltip (and
                  // the "Name: …" disambiguator line below) leaked "GAM
                  // Display" right back even though the row itself read
                  // "Display" (flank#4741 QA).
                  const fullName =
                    row[label] == null || row[label] === ''
                      ? null
                      : activeSubTab === 'channel'
                        ? genericChannelLabel(String(row[label]), canViewPlatform)
                        : String(row[label]);
                  const rowReachNote =
                    liveReach.active && liveBreakdown
                      ? liveReach.failed
                        ? liveReach.notes.failed
                        : !liveCovers(
                              row.impressions,
                              liveReach.byKey.get(liveReachKey(liveBreakdown, row[liveBreakdown])),
                            )
                          ? liveReach.notes.partial
                          : row.reach == null
                            ? liveReach.notes.withheld
                            : null
                      : reachRowNote(filters.dsps, dims, row, canViewPlatform);
                  const tooltip = needsDisambiguator
                    ? [
                        fullName && fullName !== displayName ? `Name: ${fullName}` : null,
                        `ID: ${entityId}`,
                        platformLabel
                          ? `Same label and ID as another row — different platform (${platformLabel}), not a duplicate.`
                          : 'Same label as another row — different entity (not a duplicate).',
                      ]
                        .filter(Boolean)
                        .join('\n')
                    : (fullName ?? displayName);

                  return (
                    <TableRow
                      key={entityId ? `${entityId}-${index}` : index}
                      sx={{ '&:hover': { bgcolor: COLORS.pageBg } }}
                    >
                      <TableCell sx={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                        {/*
                          Creative artwork sits beside the name rather than in its
                          own column: only one breakdown has it, and an empty
                          column on every other one reads as missing data.

                          A flex row, not inline content: inline layout wrapped the
                          image above the name — doubling the row and putting the
                          image exactly where the name's tooltip renders.
                        */}
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                          {preview?.preview_url ? (
                            <Box
                              component='img'
                              src={preview.preview_url}
                              alt=''
                              loading='lazy'
                              role={openable ? 'button' : undefined}
                              aria-label={openable ? 'Preview creative' : undefined}
                              onClick={openable ? () => setOpenPreview(preview) : undefined}
                              onError={event => {
                                // A dead asset URL should leave the name readable.
                                (event.currentTarget as HTMLImageElement).style.display = 'none';
                              }}
                              sx={{
                                width: 32,
                                height: 32,
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: `1px solid ${COLORS.border}`,
                                mr: 1,
                                flexShrink: 0,
                                cursor: openable ? 'pointer' : 'default',
                              }}
                            />
                          ) : openable ? (
                            // No stored thumbnail (typically VAST video) but the modal can
                            // still play it — same icon the Creative Library's Preview button uses.
                            <IconButton
                              size='small'
                              aria-label='Preview creative'
                              onClick={() => setOpenPreview(preview)}
                              sx={{ mr: 0.5, p: 0.25, flexShrink: 0, color: COLORS.muted }}
                            >
                              <ExportIcon className='w-[14px]' />
                            </IconButton>
                          ) : null}
                          <Tooltip
                            title={<Box sx={{ whiteSpace: 'pre-line' }}>{tooltip}</Box>}
                            arrow
                            placement='top-start'
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 0.75,
                                minWidth: 0,
                                overflow: 'hidden',
                              }}
                            >
                              <Typography
                                component='span'
                                variant='body2'
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {displayName}
                              </Typography>
                              {needsDisambiguator && (
                                <Typography
                                  component='span'
                                  variant='caption'
                                  sx={{
                                    flexShrink: 0,
                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                    color: COLORS.muted,
                                    fontWeight: 600,
                                    letterSpacing: 0.2,
                                  }}
                                >
                                  {disambiguatorText}
                                </Typography>
                              )}
                            </Box>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {showAdvertiser && (
                        <TableCell sx={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          <Tooltip title={String(row.advertiser_name ?? '')} arrow placement='top-start'>
                            <Typography
                              component='span'
                              variant='body2'
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: COLORS.muted,
                              }}
                            >
                              {dimensionLabel(row.advertiser_name)}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      )}
                      {visibleCols.map(col => {
                        // `conversionsColumn`, not `perPlatformConversions` — see
                        // the header gate above (review, CodeRabbit).
                        const gated = isGated(col, reachColumn, conversionsColumn);
                        return (
                          <TableCell
                            key={col.key}
                            align='right'
                            className={col.printHide ? 'rv2-print-hide-col' : undefined}
                            // nowrap + the widened COLS widths (#4716 follow-up)
                            // keep a large formatted value from visually
                            // running into the next column ("9,072,5851.84" on
                            // preprod QA).
                            sx={{ fontWeight: col.key === sortCol ? 600 : 400, whiteSpace: 'nowrap' }}
                          >
                            {gated ? (
                              '—'
                            ) : col.perPlatformMetric === 'reach' && liveReach.loading ? (
                              <Skeleton width={48} sx={{ ml: 'auto' }} />
                            ) : col.perPlatformMetric === 'reach' && rowReachNote === REACH_NOT_ADDITIVE_NOTE ? (
                              <Tooltip title={REACH_NOT_ADDITIVE_NOTE} placement='top' arrow>
                                <Typography
                                  component='span'
                                  variant='inherit'
                                  sx={{ color: COLORS.muted, fontStyle: 'italic' }}
                                >
                                  {NOT_ADDITIVE_LABEL}
                                </Typography>
                              </Tooltip>
                            ) : col.perPlatformMetric === 'reach' && rowReachNote ? (
                              <Tooltip title={rowReachNote} placement='top' arrow>
                                <span>—</span>
                              </Tooltip>
                            ) : (
                              cell(row[col.key], col.format)
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                {/*
                  Total row (flank#4645), pinned to the bottom of every
                  tab/chip. Values come from totalRowValues — the ungrouped
                  'totals' widget's aggregate over every row matching the
                  current filters, not just this page — so this always
                  matches the KPI cards, by construction, for the same
                  reason: both read the same query. On Inventory chips
                  (totalRowMismatch) that aggregate covers more delivery than
                  these APP/DOMAIN-only rows do — shown anyway per AC, with a
                  tooltip on the label explaining the gap (follow-up to
                  flank#4649's review, which hid it instead).
                */}
                {!totalsReady ? null : (
                  <TableRow data-testid='impressions-by-type-total-row' sx={{ bgcolor: COLORS.pageBg }}>
                    <TableCell sx={{ fontWeight: 700, borderTop: `2px solid ${COLORS.border}`, whiteSpace: 'nowrap' }}>
                      {totalRowMismatch ? (
                        <Tooltip title={totalRowMismatchNoteText} placement='top' arrow>
                          <span>{`Total · ${totalRows.toLocaleString()} rows`}</span>
                        </Tooltip>
                      ) : (
                        `Total · ${totalRows.toLocaleString()} rows`
                      )}
                    </TableCell>
                    {showAdvertiser && <TableCell sx={{ borderTop: `2px solid ${COLORS.border}` }} />}
                    {visibleCols.map(col => {
                      const restricted = Boolean(
                        col.restrictedMetric && restrictedFields.includes(`metrics.${col.restrictedMetric}`),
                      );
                      // `conversionsColumn`, not `perPlatformConversions` — matches the
                      // regular cells above (review, CodeRabbit): a Hierarchy entity
                      // breakdown without a single-platform filter shows the
                      // conversions column (conversionsColumn true) while
                      // perPlatformConversions is false, so the Total cell must say
                      // "Not additive" there too, not blank out as "—".
                      const gated = isGated(col, reachColumn, conversionsColumn);
                      const notAdditiveNote = NOT_ADDITIVE_KEYS[col.key];
                      return (
                        <TableCell
                          key={col.key}
                          align='right'
                          className={col.printHide ? 'rv2-print-hide-col' : undefined}
                          // nowrap — same overflow fix as the row cells above;
                          // the Total is often the single largest value in the column.
                          sx={{ fontWeight: 700, borderTop: `2px solid ${COLORS.border}`, whiteSpace: 'nowrap' }}
                        >
                          {restricted ? (
                            <Typography
                              component='span'
                              variant='inherit'
                              sx={{ color: COLORS.muted, fontStyle: 'italic' }}
                            >
                              Restricted
                            </Typography>
                          ) : gated ? (
                            '—'
                          ) : notAdditiveNote ? (
                            <Tooltip title={notAdditiveNote} placement='top' arrow>
                              <Typography
                                component='span'
                                variant='inherit'
                                sx={{ color: COLORS.muted, fontStyle: 'italic', fontWeight: 400 }}
                              >
                                {NOT_ADDITIVE_LABEL}
                              </Typography>
                            </Tooltip>
                          ) : (
                            cell(totalRowValues[col.key], col.format)
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {visibleCols.some(col => col.printHide) && (
              // Screen never sees this — print-only, and only when a
              // printHide column is actually in the table. Built from the
              // COLS flags rather than a fixed "CTR/VCR" string so adding
              // another printHide column (flank#4310's CPC follow-up) can't
              // leave this caption silently wrong again. Per-row values for
              // these columns aren't substituted by anything else in the
              // printed report — the KPI strip totals aren't a substitute for
              // Bonneville reading per-line-item VCR, and the KPI strip itself
              // is an optional PDF section that can be left out entirely
              // (review, Lucas, #4310).
              <Typography
                variant='caption'
                sx={{ display: 'none', displayPrint: 'block', color: COLORS.muted, mt: 0.5 }}
              >
                {visibleCols
                  .filter(col => col.printHide)
                  .map(col => col.label)
                  .join('/')}{' '}
                omitted in print — see CSV.
              </Typography>
            )}
            {openPreview && (
              <CreativePreviewModal
                open
                handleClose={() => setOpenPreview(null)}
                name={openPreview.name ?? openPreview.dsp_creative_id}
                type={openPreview.type as CreativeType}
                tagContent={openPreview.tag_content}
                fileUrl={openPreview.file_url}
                clickUrl={openPreview.click_url}
                size={openPreview.size}
              />
            )}
          </Box>
          {/* Paged, not "show more": growing the grid pushed the header off screen
              and hid how much data there was. The report endpoint now returns the
              full result-set size, so the pager can show real page numbers and a
              row range rather than guessing at the end. */}
          {hiddenImpressions > 0 && (
            <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block', pt: 1 }}>
              {`${num(hiddenImpressions)} impressions aren't listed here — some platforms don't report at this level, and some report a delivery they couldn't resolve. The delivery is real, it just has no ${tabLabel.toLowerCase()} value attached.`}
            </Typography>
          )}

          {/* A hover tooltip is invisible on a touch screen and in a screenshot
              (review, Lucas, flank#4683) — this is the same explanation as the
              Total row's tooltip, just always on, matching the other caption-style
              notes on this grid. */}
          {totalRowMismatch && totalsReady && (
            <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block', pt: 1 }}>
              {totalRowMismatchNoteText}
            </Typography>
          )}

          {geoIgnoredFilterNoteText && (
            <>
              <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block', pt: 1 }}>
                {geoIgnoredFilterNoteText}
              </Typography>
              {/* review, CodeRabbit: the note above explains the Geo rows, but not why
                  the Total can disagree with them — the ungrouped 'totals' widget isn't
                  geo-dimensioned, so it keeps the dropped filter and still counts
                  delivery these rows can't show. */}
              <Typography variant='caption' sx={{ color: COLORS.muted, display: 'block', pt: 1 }}>
                The Total above still applies that filter, so it can be lower than what the rows below add up to.
              </Typography>
            </>
          )}

          {!hideClientMoney && (
            <ClientMoneyCoverageNote
              rows={rowsWithMargin}
              restricted={restrictedFields.includes('metrics.client_spend')}
            />
          )}

          {totalRows > ROWS_PER_PAGE_OPTIONS[0] && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
                pt: 1,
              }}
            >
              <Typography variant='caption' sx={{ color: COLORS.muted }}>
                {(page * rowsPerPage + 1).toLocaleString()}–{(page * rowsPerPage + rows.length).toLocaleString()} of{' '}
                {totalRows.toLocaleString()} rows
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography variant='caption' sx={{ color: COLORS.muted }}>
                    Rows
                  </Typography>
                  <ToggleButtonGroup
                    size='small'
                    exclusive
                    value={rowsPerPage}
                    onChange={(_e, v) => v && handleRowsPerPageChange(v)}
                    sx={darkToggleSx}
                  >
                    {ROWS_PER_PAGE_OPTIONS.map(n => (
                      <ToggleButton key={n} value={n}>
                        {n}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
                <Box className='rv2-noprint' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Button
                    size='small'
                    disabled={page === 0}
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
                  >
                    Prev
                  </Button>
                  {pageWindow(page, pageCount).map((n, i) =>
                    n === ELLIPSIS ? (
                      // Keyed by position: the gaps are structural, not data.
                      <Typography key={`gap-${i}`} variant='caption' sx={{ color: COLORS.muted, px: 0.5 }}>
                        …
                      </Typography>
                    ) : (
                      <Button
                        key={n}
                        size='small'
                        onClick={() => setPage(n)}
                        sx={{
                          textTransform: 'none',
                          minWidth: 32,
                          px: 1,
                          color: n === page ? '#fff' : COLORS.muted,
                          bgcolor: n === page ? COLORS.black : 'transparent',
                          '&:hover': { bgcolor: n === page ? '#000' : COLORS.pageBg },
                        }}
                      >
                        {n + 1}
                      </Button>
                    ),
                  )}
                  <Button
                    size='small'
                    disabled={!hasNextPage}
                    onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))}
                    sx={{ textTransform: 'none', minWidth: 0, color: COLORS.muted }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ImpressionsByType;
